/**
 * `localStorage` that never throws. Merely *reading* `window.localStorage`
 * throws a SecurityError ("Access is denied for this document") when the
 * browser blocks site data (sandboxed/third-party contexts, strict privacy
 * settings, some managed browsers), and setItem throws on a full quota.
 * Several call sites run inside render (react-query `initialData`), where a
 * throw takes down the whole app as a CriticalApplicationError.
 *
 * Reads return `null` and writes are dropped when storage is unavailable, so
 * callers degrade to "nothing remembered" instead of crashing.
 */
export const safeLocalStorage = {
  getItem(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key: string, value: string): void {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Storage blocked or full — nothing to persist to.
    }
  },
  /** Number of stored keys; 0 when storage is unavailable. */
  length(): number {
    try {
      return window.localStorage.length;
    } catch {
      return 0;
    }
  },
  key(index: number): string | null {
    try {
      return window.localStorage.key(index);
    } catch {
      return null;
    }
  },
  clear(): void {
    try {
      window.localStorage.clear();
    } catch {
      // Storage blocked — nothing to clear.
    }
  },
  removeItem(key: string): void {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Storage blocked — nothing to remove.
    }
  },
};
